import React, { KeyboardEvent, ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import Container from '@ui/container/container';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Text from '@ui/text/text/text';

interface WatermarkAxisInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  prefix?: string;
  name?: string;
  allowNegative?: boolean;
  disabled?: boolean;
}

const WatermarkAxisInput: React.FC<WatermarkAxisInputProps> = ({
  value,
  onChange,
  label,
  prefix,
  name,
  allowNegative = true,
  disabled = false,
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) {
      setDisplayValue(value);
    }
  }, [value]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const newValue = e.target.value;

      if (newValue === '' || newValue === '-') {
        setDisplayValue(newValue);
        return;
      }

      const regex = allowNegative ? /^-?\d*$/ : /^\d*$/;

      if (regex.test(newValue)) {
        const sanitized =
          newValue.length > 1 && newValue.startsWith('0') && !newValue.startsWith('0.')
            ? String(parseInt(newValue, 10))
            : newValue;

        setDisplayValue(sanitized);
        onChange(sanitized);
      }
    },
    [allowNegative, onChange]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const currentNum = parseInt(displayValue, 10) || 0;
        const nextNum = e.key === 'ArrowUp' ? currentNum + step : currentNum - step;
        const next = String(nextNum);

        setDisplayValue(next);
        onChange(next);
      }
    },
    [displayValue, onChange]
  );

  const handleFocus = useCallback(() => {
    isFocused.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    isFocused.current = false;
    if (displayValue === '' || displayValue === '-') {
      onChange('0');
      setDisplayValue('0');
    }
  }, [displayValue, onChange]);

  const renderInner = useCallback(() => {
    if (!prefix) return undefined;
    return (
      <Container className="flex flex-1 jc-flex-start">
        <Text className="P2" color="var(--global-ico-text-3)">
          {prefix}
        </Text>
      </Container>
    );
  }, [prefix]);

  return (
    <InputWithOverlay
      jsxLabel={() => (
        <Text className="L2 padding-l-16 margin-b-2" design="GlobalIcoText3">
          {label}
        </Text>
      )}
      inputHeight="36px"
      innerContentClassName="padding-h-12"
      enableChildrenPointerEvents={false}
      inputWidth="100%"
      wrapperClass="flex-1 as-flex-end"
      inputProps={{
        autoComplete: 'off',
        name: name || (label ? `${label.toLowerCase()}-watermark` : 'axis-input'),
        type: 'text',
        inputMode: 'numeric',
        maxLength: 4,
        value: displayValue,
        onKeyDown,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        className: `P2 padding-r-0 ${prefix ? 'padding-l-30' : ''}`,
        disabled,
        style: disabled ? { opacity: 0.6 } : undefined,
      }}
      renderInnerContent={prefix ? renderInner : undefined}
    />
  );
};

export default WatermarkAxisInput;
